import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { db, storage } from '../firebase/config';
import { ref, getBlob } from 'firebase/storage';
import { ArrowLeft, Printer, Download, Edit2, FileArchive, Image, X, ZoomIn, FileText, Paperclip, ChevronDown } from 'lucide-react';
import PageHeader from './common/PageHeader';
import { remakeCauseLabel } from '../utils/remakeCauses';
import { remakeLabelOf } from '../utils/remakeKind';
import RemakeKindModal from './common/RemakeKindModal';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Document, Page, pdfjs } from 'react-pdf';
import { useAuth } from '../context/AuthContext';
import StlViewer from './StlViewer';
import LabelPrintModal from './LabelPrintModal';
import { buildLabelData } from '../utils/labelData';
import { markOrderReceivedStage } from '../utils/productionStage';
import { QRCodeSVG } from 'qrcode.react';
import { getOrCreateToken, tokenUrl } from '../utils/caseToken';
import { maskName } from '../utils/outsourceShared';
import { createNotification } from '../services/NotificationSystem';
import OrderFeedbackCard from './clinic/OrderFeedbackCard';   // 🩺 「장착 후 확인」 카드 (2026-10-02, 플래그 patientFeedback strict — 카드 안에서 게이트, OFF면 CF 호출 0)
import OrderReceptionLink from './clinic/OrderReceptionLink';   // 🧾 「오늘 접수」 연결 표시·해제 (2026-10-03, 플래그 deskReception strict — 컴포넌트 안에서 게이트)
import OrderPatientLink from './clinic/PatientOrderLink';   // 🦷 저장된 의뢰서 ↔ 환자 명부 [환자 연결] (2026-10-17 점검 P1-1 — 컴포넌트 안에서 치과·isClinicPatientsOn 게이트)
import { orderKindLines } from '../utils/orderKindLines';   // 의뢰서 보철 목록 — 종류마다 한 줄 (2026-11-02)

// PDF.js worker 설정 - 로컬 파일 사용
pdfjs.GlobalWorkerOptions.workerSrc = 
    `https://unpkg.com/pdfjs-dist@5.4.296/build/pdf.worker.min.mjs`;

function ViewOrder({ orderId, user, onBack }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { currentUser } = useAuth(); 
    const [order, setOrder]= useState(null);
    const [loading, setLoading] = useState(true);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [senderInfo, setSenderInfo] = useState(null);
    const [receiverInfo, setReceiverInfo] = useState(null);
    const printRef = useRef(null);
    const [previewImage, setPreviewImage] = useState(null);
    // 회신 템플릿 v1 (2026-08-25 에이전트 화면 기준안 P1) — 수신 기공소 → 치과
    const [replyOpen, setReplyOpen] = useState(false);
    const [replyType, setReplyType] = useState('question');   // 'reject' | 'question' | 'revision'
    const [replyPreset, setReplyPreset] = useState('');
    const [replyText, setReplyText] = useState('');
    const [replySending, setReplySending] = useState(false);
    const [remakePick, setRemakePick] = useState(null);   // [리메이크] 재제작/수리 선택 모달 (2026-09-04)

    // QR 토큰 발급 — 수신 기공소 계정 + 필기 아님일 때만 (기획서_QR공정체크 §5-4)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                if (!order || !order.id) return;
                if (order.orderType === 'handwritten' || order.source === 'handwritten') { setQrToken(null); return; }
                const myId = user?.companyId || user?.uid;
                const bt = user?.businessType || user?.companyBusinessType;
                const isLab = bt === 'lab' || bt === '기공소';
                if (!isLab || !myId || order.toUserId !== myId) { setQrToken(null); return; }
                const t = await getOrCreateToken(myId, { origin: 'order', id: order.id });
                if (!cancelled) setQrToken(t || 'failed');   // 실패 시 'failed' → "QR 없이 인쇄됩니다" 안내
            } catch (e) {
                console.error('QR 토큰 준비 실패:', e?.code || e?.message);
                if (!cancelled) setQrToken('failed');
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order?.id, order?.toUserId, user?.uid, user?.companyId]);
    const [stlPreview, setStlPreview] = useState(null);      // { url, fileName } | { entries, title, notice } (디자인 미리보기)
    const [linkedScan, setLinkedScan] = useState(null);      // 통합 에이전트 스캔 (order.scanId)
    const [activeTab, setActiveTab] = useState(null); // 초기값은 데이터 로드 후 설정
    const [showLabelModal, setShowLabelModal] = useState(false);
    // QR 공정 체크 토큰 (2026-08-18) — 수신 기공소가 볼 때만 발급. 필기 의뢰서 제외(대표 지시).
    // null=발급 실패/미대상, ''=발급 중. QR엔 환자정보가 들어가지 않는다(불투명 토큰).
    const [qrToken, setQrToken] = useState('');

    // 통합 에이전트 스캔 로드 (경유-삭제 전까지만 archive 존재)
    useEffect(() => {
        if (!order?.scanId) { setLinkedScan(null); return; }
        getDoc(doc(db, 'scans', order.scanId))
            .then(snap => setLinkedScan(snap.exists() ? { id: snap.id, ...snap.data() } : null))
            .catch(e => console.error('스캔 로드 실패:', e));
    }, [order?.scanId]);
    const [numPages, setNumPages] = useState(null);
    const [loadedPages, setLoadedPages] = useState(1);
    const [pdfError, setPdfError] = useState(false);

    const fromChat = location.state?.fromChat;
    const chatRoomId = location.state?.chatRoomId;
    const fromCalendar = location.state?.fromCalendar;

    // PDF options 메모이제이션
    const pdfOptions = useMemo(() => ({
    cMapUrl: 'https://unpkg.com/pdfjs-dist@5.4.296/cmaps/',
    cMapPacked: true,
}), []);

    const handleFileDownload = async (url, filename, isZip = false) => {
        // 다운로드 실행 = 제작 현황 '의뢰서확인' 진입 (zip은 새 창 열기라 실제 저장 완료까지는 확인 불가 — 실행 시점 기록)
        markOrderReceivedStage(order, user);
        if (isZip) {
            window.open(url, '_blank'); 
            return; 
        }
        try {
            const pathMatch = url.match(/\/o\/(.+?)\?/);
            if (pathMatch) {
                const storagePath = decodeURIComponent(pathMatch[1]);
                const fileRef = ref(storage, storagePath);
                const blob = await getBlob(fileRef);
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename || 'download';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            } else { 
                window.open(url, '_blank'); 
            }
        } catch (error) { 
            console.error('다운로드 실패:', error); 
            alert('다운로드에 실패했습니다.'); 
        }
    };

    const handleBack = () => {
        if (fromCalendar) navigate('/calendar');
        else if (fromChat && chatRoomId) navigate('/chat/' + chatRoomId);
        else if (onBack) onBack();
        else navigate('/orders');
    };

    useEffect(() => { 
    if (orderId && !order) {
        fetchOrder(); 
    }
}, [orderId]); 

    const fetchOrder = async () => {
        try {
            setLoading(true);
            const orderDoc = await getDoc(doc(db, 'workOrders', orderId));
            if (orderDoc.exists()) {
                const orderData = { id: orderDoc.id, ...orderDoc.data() };
              
                setOrder(orderData);

                // 기본 탭 설정: PDF가 있으면 의뢰서 탭, 없으면 클러스터 탭
                const hasPdf = orderData.attachments?.some(a => a.type === 'pdf');
                setActiveTab(hasPdf ? 'original' : 'cluster');

                const senderDoc = await getDoc(doc(db, 'users', orderData.fromUserId));
                if (senderDoc.exists()) setSenderInfo(senderDoc.data());
                
                const receiverDoc = await getDoc(doc(db, 'users', orderData.toUserId));
                if (receiverDoc.exists()) setReceiverInfo(receiverDoc.data());
                
                const targetId = user?.companyId || user?.uid;
                if (targetId === orderData.toUserId && !orderData.readByReceiver) {
                    await updateDoc(doc(db, 'workOrders', orderId), {
                        readByReceiver: true,
                        readAt: serverTimestamp()
                    });
                }
                // 열람 = 제작 현황 '의뢰서확인' 진입 (2026-08-05 대표 지시 — 열람·인쇄·다운로드·CAD 중 하나)
                // readByReceiver 갱신 시점뿐 아니라 이미 읽음 처리된 과거 주문 재열람도 포함
                // (processStage 있으면 미기록·수신자만·메시지형 제외 가드는 util 내부에서 처리)
                markOrderReceivedStage(orderData, user);
            }
        } catch (error) { 
            console.error('의뢰서 조회 실패:', error); 
        }
        finally { 
            setLoading(false); 
        }
    };

    useEffect(() => {
        if (!order) return;
        const isEmailParsed = ['dentweb', 'email', 'r2cad'].includes(order.source);
        const pdfFile = order.attachments?.find(a => a.type === 'pdf');
        if (isEmailParsed && pdfFile) { 
            setActiveTab('original'); 
            setNumPages(null);
            setLoadedPages(1);
            setPdfError(false);
        }
        else { 
            setActiveTab('cluster'); 
        }
    }, [order]);

    const handlePdfLoadSuccess = ({ numPages }) => {
        console.log('✅ PDF 로딩 완료:', numPages, '페이지');
        setNumPages(numPages);
        setPdfError(false);
        
        let currentPage = 1;
        const loadInterval = setInterval(() => {
            currentPage += 2;
            if (currentPage >= numPages) {
                clearInterval(loadInterval);
                setLoadedPages(numPages);
            } else {
                setLoadedPages(currentPage);
            }
        }, 200);
    };

    const handlePdfLoadError = (error) => {
        console.error('❌ PDF 로딩 실패:', error);
        setPdfError(true);
        setNumPages(null);
    };

    const handlePrint = async () => {
        const isEmailParsed = ['dentweb', 'email', 'r2cad'].includes(order.source);
        const pdfFile = order.attachments?.find(a => a.type === 'pdf');

        // 인쇄 기록 - 비동기로 처리 (인쇄 다이얼로그 지연 방지)
        const targetId = user?.companyId || user?.uid;
        const updateData = {
            printedAt: serverTimestamp(),
            printedBy: user?.uid || null
        };
        if (targetId === order.toUserId && order.status === 'pending') {
            updateData.status = 'in_progress';
        }
        updateDoc(doc(db, 'workOrders', orderId), updateData)
            .catch(error => console.error('인쇄 기록 실패:', error));
        markOrderReceivedStage(order, user);   // 인쇄 실행 = 제작 현황 '의뢰서확인' 진입

        // 이메일 파싱된 PDF: Blob으로 받아서 바로 인쇄
        if (isEmailParsed && pdfFile) {
            try {
                // PDF를 Blob으로 다운로드
                const pathMatch = pdfFile.url.match(/\/o\/(.+?)\?/);
                if (pathMatch) {
                    const storagePath = decodeURIComponent(pathMatch[1]);
                    const fileRef = ref(storage, storagePath);
                    const blob = await getBlob(fileRef);
                    const blobUrl = window.URL.createObjectURL(blob);
                    
                    // iframe으로 인쇄
                    const iframe = document.createElement('iframe');
                    iframe.style.display = 'none';
                    iframe.src = blobUrl;
                    document.body.appendChild(iframe);
                    
                    iframe.onload = () => {
                        setTimeout(() => {
                            iframe.contentWindow.print();
                            // 인쇄 후 정리
                            setTimeout(() => {
                                document.body.removeChild(iframe);
                                window.URL.revokeObjectURL(blobUrl);
                            }, 1000);
                        }, 500);
                    };
                } else {
                    // URL 파싱 실패시 새 탭으로
                    window.open(pdfFile.url, '_blank')?.print();
                }
            } catch (error) {
                console.error('PDF 인쇄 실패:', error);
                // 실패시 새 탭으로 fallback
                window.open(pdfFile.url, '_blank')?.print();
            }
            return;
        } 
        // 클러스터 의뢰서: 일반 인쇄
        else {
            const prevTab = activeTab;
            if (activeTab !== 'cluster') { 
                setActiveTab('cluster'); 
                await new Promise(r => setTimeout(r, 200)); 
            }
            window.print();
            if (prevTab !== 'cluster') {
                setTimeout(() => setActiveTab(prevTab), 500);
            }
        }
    };

    const handleDownloadPDF = async () => {
        const isEmailParsed = ['dentweb', 'email', 'r2cad'].includes(order.source);
        const pdfFile = order.attachments?.find(a => a.type === 'pdf');
        
        if (isEmailParsed && pdfFile) {
            try { 
                await updateDoc(doc(db, 'workOrders', orderId), { 
                    downloadedPdfAt: serverTimestamp(), 
                    downloadedPdfBy: user?.uid || null 
                }); 
            } catch(e) { 
                console.error('다운로드 기록 실패:', e); 
            }
            await handleFileDownload(
                pdfFile.url, 
                (order.patientName || '환자') + '_기공의뢰서_원본.pdf'
            );
            return;
        }
        
        if (!printRef.current) return;
        
        try {
            setPdfLoading(true);
            const canvas = await html2canvas(printRef.current, { 
                scale: 2, 
                useCORS: true, 
                backgroundColor: '#ffffff' 
            });
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const ratio = Math.min(pdfWidth / canvas.width, pdfHeight / canvas.height);
            pdf.addImage(
                imgData,
                'PNG',
                0,
                0,
                canvas.width * ratio,
                canvas.height * ratio
            );
            pdf.save('의뢰서_' + (order.patientName || '환자') + '_' + formatDate(order.createdAt) + '.pdf');
            markOrderReceivedStage(order, user);   // 다운로드 실행 = 제작 현황 '의뢰서확인' 진입
            try {
                await updateDoc(doc(db, 'workOrders', orderId), {
                    downloadedPdfAt: serverTimestamp(),
                    downloadedPdfBy: user?.uid || null
                });
            } catch (e) {
                console.error('다운로드 기록 실패:', e);
            }
        } catch (error) {
            console.error('PDF 생성 실패:', error);
            alert('PDF 생성에 실패했습니다.');
        }
        finally {
            setPdfLoading(false);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        let date = timestamp.toDate ? timestamp.toDate() : timestamp instanceof Date ? timestamp : new Date(timestamp);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('ko-KR', { 
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit' 
        }).replace(/\. /g, '.').replace(/\.$/, '');
    };

    const formatShortDate = (timestamp, withWeekday = false) => {
        if (!timestamp) return '-';
        let date = timestamp.toDate ? timestamp.toDate() : timestamp instanceof Date ? timestamp : new Date(timestamp);
        if (isNaN(date.getTime())) return '-';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const weekdays = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];
        return withWeekday 
            ? year + '.' + month + '.' + day + ' ' + weekdays[date.getDay()] 
            : year + '.' + month + '.' + day;
    };

    const sortTeethByChartOrder = (teeth) => {
        if (!teeth || teeth.length === 0) return [];
        const chartOrder = [
            18, 17, 16, 15, 14, 13, 12, 11, 
            21, 22, 23, 24, 25, 26, 27, 28, 
            48, 47, 46, 45, 44, 43, 42, 41, 
            31, 32, 33, 34, 35, 36, 37, 38
        ];
        return [...teeth].sort((a, b) => chartOrder.indexOf(a) - chartOrder.indexOf(b));
    };

    const getShadeText = () => {
        if (!order.items || order.items.length === 0) return order.shade || '-';
        const shades = [...new Set(order.items.map(i => i.shade).filter(s => s && s !== '-'))];
        return shades.length > 0 ? shades.join(', ') : '-';
    };

    const getShadeTypeText = () => {
        if (!order.items || order.items.length === 0) return '';
        const types = [...new Set(order.items.map(i => i.shadeType).filter(s => s))];
        return types.length > 0 ? types.join(', ') : '';
    };

    const isPonticTooth = (toothNumber) =>
        order.ponticGroups?.some(g => g.teeth?.includes(toothNumber))
        || order.items?.some(i => i.toothNumber === toothNumber && i.isPontic)
        || false;

    const getAttachmentCount = () =>
        (order?.attachments || []).filter(a => a.type === 'zip' || a.type === 'image' || a.type === 'file').length
        + (order?.imageUrls || []).length;

    const cleanSpecialNotes = (notes) => {
        if (!notes) return '없음';
        let c = notes;
        // 앞부분 메타데이터 제거 (환자정보, 납품일 등)
        c = c.replace(/^.*?(?=<쉐이드>|<Cr|<인레이|<임플란트)/is, '');
        // 하단 법률 문구 제거
        c = c.replace(/의료기사 등에 관한 법률[\s\S]*/g, '');
        c = c.replace(/Tel:[^\n]*/gi, '');
        c = c.replace(/Fax:[^\n]*/gi, '');
        c = c.replace(/^작성:.*$/gm, '');
        c = c.replace(/^의뢰 치과:.*$/gm, '');
        // 치식도 숫자 잔여물만 제거
        c = c.replace(/^\d{1,2}\s*$/gm, '');
        // 정리
        c = c.replace(/\n{3,}/g, '\n\n').trim();
        return c || '없음';
    };

    const renderAttachmentsTab = () => {
        const attachments = order?.attachments || [];
        const zipFiles = attachments.filter(a => a.type === 'zip');
        const genericFiles = attachments.filter(a => a.type === 'file');   // 일반 파일 첨부 (2026-08-12)
        const imageFiles = attachments.filter(a => a.type === 'image');

        // imageUrls 배열도 이미지로 표시 (앱에서 업로드한 이미지)
        const imageUrlFiles = (order?.imageUrls || []).map((url, i) => ({
            url,
            type: 'image',
            filename: `첨부이미지_${i + 1}.jpg`,
        }));
        const allImageFiles = [...imageFiles, ...imageUrlFiles];

        if (zipFiles.length === 0 && allImageFiles.length === 0 && genericFiles.length === 0) {
            return (
                <div style={styles.emptyAttachments}>
                    <Paperclip size={48} color="#cbd5e1" />
                    <p style={{ marginTop: '12px', color: '#94a3b8' }}>첨부파일이 없습니다</p>
                </div>
            );
        }
        
        return (
            <div style={styles.attachmentsTab}>
                {genericFiles.length > 0 && (
                    <div style={styles.fileSection}>
                        <div style={styles.fileSectionTitle}>
                            <Paperclip size={20} color="#0ea5e9" />
                            <span>첨부 파일</span>
                        </div>
                        {genericFiles.map((file, i) => (
                            <div key={'g' + i} style={styles.fileItem}>
                                <div style={{ ...styles.fileIcon, backgroundColor: '#0ea5e9' }}>{(file.filename || file.name || '').split('.').pop().slice(0, 4).toUpperCase() || 'FILE'}</div>
                                <div style={styles.fileInfo}>
                                    <div style={styles.fileName}>{file.filename || file.name}</div>
                                    <div style={styles.fileSize}>{file.size ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : ''}</div>
                                </div>
                                <button onClick={() => handleFileDownload(file.url, file.filename || file.name, true)} style={styles.fileDownloadBtn}>
                                    <Download size={16} /> 다운로드
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                {zipFiles.length > 0 && (
                    <div style={styles.fileSection}>
                        <div style={styles.fileSectionTitle}>
                            <FileArchive size={20} color="#6366f1" />
                            <span>ZIP 파일</span>
                        </div>
                        {zipFiles.map((file, i) => (
                            <div key={i} style={styles.fileItem}>
                                <div style={{ ...styles.fileIcon, backgroundColor: '#6366f1' }}>ZIP</div>
                                <div style={styles.fileInfo}>
                                    <div style={styles.fileName}>{file.filename || file.name}</div>
                                    <div style={styles.fileSize}>
                                        {file.size ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : ''}
                                    </div>
                                </div>
                                <button 
                                    onClick={() => handleFileDownload(file.url, file.filename || file.name, true)} 
                                    style={styles.fileDownloadBtn}
                                >
                                    <Download size={16} /> 다운로드
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                
                {allImageFiles.length > 0 && (
                    <div style={styles.fileSection}>
                        <div style={styles.fileSectionTitle}>
                            <Image size={20} color="#10b981" />
                            <span>이미지 파일 ({allImageFiles.length})</span>
                        </div>
                        <div style={styles.imageGridTab}>
                            {allImageFiles.map((file, i) => (
                                <div key={i} style={styles.imageItemTab}>
                                    <img 
                                        src={file.url} 
                                        alt={file.filename || file.name} 
                                        style={styles.imageThumbnailTab} 
                                        onClick={() => setPreviewImage(file)} 
                                    />
                                    <div style={styles.imageOverlayTab}>
                                        <button 
                                            onClick={() => setPreviewImage(file)} 
                                            style={styles.imageBtnTab}
                                        >
                                            <ZoomIn size={16} />
                                        </button>
                                        <button 
                                            onClick={() => handleFileDownload(file.url, file.filename || file.name)} 
                                            style={styles.imageBtnTab}
                                        >
                                            <Download size={16} />
                                        </button>
                                    </div>
                                    <div style={styles.imageNameTab}>{file.filename || file.name}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                <div style={{ 
                    marginTop: '20px', 
                    padding: '12px', 
                    backgroundColor: '#fef3c7', 
                    borderRadius: '8px', 
                    fontSize: '13px', 
                    color: '#92400e' 
                }}>
                    첨부파일은 주문일로부터 30일간 보관 후 자동 삭제됩니다.
                </div>
            </div>
        );
    };

    const renderToothChart = () => {
        const selectedTeeth = order.items
            ? order.items.map(i => i.toothNumber)
            : (order.selectedTeeth || []);
        const selectedSet = new Set(selectedTeeth);

        const ToothCell = ({ num }) => {
            const digit = num % 10;
            const isSelected = selectedSet.has(num);
            return (
                <div style={{
                    width: '10px',
                    height: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isSelected ? '11px' : '7px',
                    fontWeight: isSelected ? '700' : '400',
                    color: isSelected ? '#000' : '#ccc',
                }}>
                    {digit}
                </div>
            );
        };

        return (
            <div style={{ display: 'inline-flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ display: 'flex' }}>
                        {[18, 17, 16, 15, 14, 13, 12, 11].map(t => <ToothCell key={t} num={t} />)}
                    </div>
                    <div style={{ width: '1px', height: '18px', backgroundColor: '#333' }}></div>
                    <div style={{ display: 'flex' }}>
                        {[21, 22, 23, 24, 25, 26, 27, 28].map(t => <ToothCell key={t} num={t} />)}
                    </div>
                </div>
                <div style={{ width: '100%', height: '1px', backgroundColor: '#333' }}></div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ display: 'flex' }}>
                        {[48, 47, 46, 45, 44, 43, 42, 41].map(t => <ToothCell key={t} num={t} />)}
                    </div>
                    <div style={{ width: '1px', height: '12px', backgroundColor: '#333' }}></div>
                    <div style={{ display: 'flex' }}>
                        {[31, 32, 33, 34, 35, 36, 37, 38].map(t => <ToothCell key={t} num={t} />)}
                    </div>
                </div>
            </div>
        );
    };

    const getGroupedProsthesisInfo = () => {
        // items가 없으면 prosthesisGroups에서 직접 생성
        if ((!order.items || order.items.length === 0) && order.prosthesisGroups?.length > 0) {
            const bridgeTeethSet = new Set();
            order.bridgeGroups?.forEach(g => {
                const teeth = typeof g.teeth === 'string' ? g.teeth.split('-').map(Number) : (g.teeth || []);
                teeth.forEach(t => bridgeTeethSet.add(t));
            });

            const result = [];
            order.prosthesisGroups.forEach(g => {
                const teeth = typeof g.teeth === 'string' ? g.teeth.split('-').map(Number) : (g.teeth || []);
                const bridgeT = teeth.filter(t => bridgeTeethSet.has(t));
                const singleT = teeth.filter(t => !bridgeTeethSet.has(t));

                if (bridgeT.length >= 2) {
                    result.push({
                        teeth: '#' + bridgeT.join(', #'),
                        type: `브릿지 ${bridgeT.length}본`,
                        prosthesis: g.prosthesis || '-',
                        material: g.material || '-',
                        pontic: null,
                    });
                }
                if (singleT.length > 0) {
                    result.push({
                        teeth: '#' + singleT.join(', #'),
                        type: '싱글',
                        prosthesis: g.prosthesis || '-',
                        material: g.material || '-',
                        pontic: null,
                    });
                }
            });

            // 임플란트 정보도 추가
            order.implantGroups?.forEach(g => {
                const teeth = typeof g.teeth === 'string' ? g.teeth.split('-').map(Number) : (g.teeth || []);
                if (teeth.length > 0) {
                    result.push({
                        teeth: '#' + teeth.join(', #'),
                        type: '임플란트',
                        prosthesis: 'Abutment',
                        material: g.brand || '-',
                        pontic: null,
                    });
                }
            });

            return result;
        }

        if (!order.items || order.items.length === 0) return [];

        const prosthesisMap = {};
        order.prosthesisGroups?.forEach(g => {
            const teeth = typeof g.teeth === 'string'
                ? g.teeth.split('-').map(Number)
                : (g.teeth || []);
            teeth.forEach(t => prosthesisMap[t] = {
                prosthesis: g.prosthesis,
                material: g.material
            });
        });

        // 브릿지 치아 집합
        const bridgeTeethSet = new Set();
        order.bridgeGroups?.forEach(g => {
            const teeth = typeof g.teeth === 'string'
                ? g.teeth.split('-').map(Number)
                : (g.teeth || []);
            teeth.forEach(t => bridgeTeethSet.add(t));
        });

        const groups = {};
        order.items.forEach(item => {
            const info = prosthesisMap[item.toothNumber];
            const prosthesis = info?.prosthesis || item.prosthesis || '-';
            const material = info?.material || item.material || '-';
            const key = prosthesis + '|' + material;
            if (!groups[key]) {
                groups[key] = { prosthesis, material, teeth: [], pontic: [], bridge: [], single: [] };
            }
            groups[key].teeth.push(item.toothNumber);
            if (item.isPontic || isPonticTooth(item.toothNumber)) {
                groups[key].pontic.push(item.toothNumber);
            }
            if (bridgeTeethSet.has(item.toothNumber)) {
                groups[key].bridge.push(item.toothNumber);
            } else {
                groups[key].single.push(item.toothNumber);
            }
        });

        const result = [];
        Object.values(groups).forEach(g => {
            // 싱글 치아
            if (g.single.length > 0) {
                const ponticInSingle = g.single.filter(t => isPonticTooth(t));
                result.push({
                    teeth: '#' + sortTeethByChartOrder(g.single).join(', #'),
                    type: '싱글',
                    prosthesis: g.prosthesis,
                    material: g.material,
                    pontic: ponticInSingle.length > 0 ? '#' + ponticInSingle.join(', #') : null,
                });
            }
            // 브릿지 치아 (bridgeGroups 단위로 분리)
            if (g.bridge.length > 0) {
                const bridgeGroupsForThis = [];
                order.bridgeGroups?.forEach(bg => {
                    const bgTeeth = typeof bg.teeth === 'string'
                        ? bg.teeth.split('-').map(Number)
                        : (bg.teeth || []);
                    const overlap = bgTeeth.filter(t => g.bridge.includes(t));
                    if (overlap.length > 0) {
                        // 11-02 대표 "치식이 중복되서 나오지 않게": 브릿지 전체 치아가 아니라 이 보철 종류에 해당하는 치아만 (폰틱도 그 안에서만)
                        //   예) 한 브릿지에 지르코니아·PFM 이 섞이면 두 줄이 같은 #18-#13 을 반복하던 것 → 각자 자기 치아만
                        const sorted = sortTeethByChartOrder(overlap);
                        const ponticInBridge = overlap.filter(t => isPonticTooth(t));
                        bridgeGroupsForThis.push({
                            teeth: '#' + sorted.join(', #'),
                            type: '브릿지',
                            prosthesis: g.prosthesis,
                            material: g.material,
                            pontic: ponticInBridge.length > 0 ? '#' + ponticInBridge.join(', #') : null,
                        });
                    }
                });
                // 중복 제거
                const seen = new Set();
                bridgeGroupsForThis.forEach(bg => {
                    if (!seen.has(bg.teeth)) {
                        seen.add(bg.teeth);
                        result.push(bg);
                    }
                });
            }
        });
        return result;
    };

    // eslint-disable-next-line no-unused-vars -- 11-02 보철 목록은 orderKindLines 로 바꿈(임플란트 포함). 다른 화면 확인 전까지 남겨 둠
    const getImplantInfo = () => {
        // 치아별 임플란트 목록 반환: [{tooth: 12, brand: 'Osstem TS Mini'}, ...]
        const result = [];
        // implantGroups에서 추출
        if (order.implantGroups && order.implantGroups.length > 0) {
            order.implantGroups.forEach(g => {
                const brand = g.brand || '-';
                (g.teeth || []).forEach(t => result.push({ tooth: t, brand }));
            });
        }
        // items[].implant에서 추출 (fallback 또는 보완)
        if (order.items) {
            const existingTeeth = new Set(result.map(r => r.tooth));
            order.items.forEach(item => {
                const implant = item.implant;
                if (implant && implant !== '' && implant !== '없음' && !existingTeeth.has(item.toothNumber)) {
                    result.push({ tooth: item.toothNumber, brand: implant });
                }
            });
        }
        return result;
    };

    if (loading) {
        return (
            <div style={styles.loading}>
                <div style={styles.spinner}></div>
                <p>로딩 중...</p>
            </div>
        );
    }
    
    if (!order) {
        return (
            <div style={styles.error}>
                <p>의뢰서를 찾을 수 없습니다.</p>
                <button onClick={onBack} style={styles.backButton}>목록으로</button>
            </div>
        );
    }

    // senderName을 미리 선언 (message 타입에서도 사용)
    const senderName = order.clinicName || senderInfo?.businessName || 
        senderInfo?.companyName || senderInfo?.name || '알 수 없음';
    const receiverName = receiverInfo?.businessName || receiverInfo?.companyName || 
        receiverInfo?.name || '알 수 없음';

    if (order.type === 'message') {
    return (
        <>
            <style>
                {`@media print { 
                    html, body { 
                        margin: 0 !important; 
                        padding: 0 !important; 
                        background: white !important; 
                    } 
                    body * { visibility: hidden; } 
                    .print-area, .print-area * { visibility: visible; } 
                    .print-area { 
                        position: absolute; 
                        left: 0; 
                        top: 0; 
                        width: 190mm !important; 
                        padding: 10mm !important; 
                        box-shadow: none !important; 
                    } 
                    .no-print { display: none !important; } 
                }`}
            </style>
            
            <div style={styles.container}>
                <div className="no-print">
                    <PageHeader
                        icon={FileText}
                        title="주문 상세"
                        actions={
                            <button onClick={handleBack} style={styles.backBtn}>
                                <ArrowLeft size={18} /> 뒤로가기
                            </button>
                        }
                    />
                </div>

                <div style={{
                    maxWidth: '210mm',
                    margin: '0 auto',
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    padding: '40px',
                    minHeight: '400px'
                }} className="print-area">
                    {/* 메시지 헤더 */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '24px',
                        paddingBottom: '20px',
                        borderBottom: '2px solid #e2e8f0'
                    }}>
                        <div style={{
                            width: '48px',
                            height: '48px',
                            backgroundColor: '#f3e8ff',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '24px'
                        }}>
                            💬
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{
                                fontSize: '14px',
                                color: '#6b21a8',
                                fontWeight: '600',
                                marginBottom: '4px'
                            }}>
                                이메일 메시지
                            </div>
                            <h1 style={{
                                margin: 0,
                                fontSize: '24px',
                                fontWeight: '700',
                                color: '#1e293b'
                            }}>
                                {order.emailSubject || order.emailInfo?.emailSubject || '제목 없음'}
                            </h1>
                        </div>
                    </div>
                    
                    {/* 메시지 정보 */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '16px',
                        marginBottom: '32px',
                        padding: '20px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '8px'
                    }}>
                        <div>
                            <div style={{
                                fontSize: '13px',
                                color: '#64748b',
                                fontWeight: '600',
                                marginBottom: '4px'
                            }}>
                                발신
                            </div>
                            <div style={{
                                fontSize: '16px',
                                color: '#1e293b',
                                fontWeight: '600'
                            }}>
                                {senderName}
                            </div>
                            <div style={{
                                fontSize: '13px',
                                color: '#94a3b8',
                                marginTop: '2px'
                            }}>
                                {order.sourceEmail || order.emailInfo?.sourceEmail}
                            </div>
                        </div>
                        <div>
                            <div style={{
                                fontSize: '13px',
                                color: '#64748b',
                                fontWeight: '600',
                                marginBottom: '4px'
                            }}>
                                수신일
                            </div>
                            <div style={{
                                fontSize: '16px',
                                color: '#1e293b',
                                fontWeight: '600'
                            }}>
                                {formatShortDate(order.emailReceivedAt || order.emailInfo?.emailReceivedAt || order.createdAt, true)}
                            </div>
                        </div>
                    </div>
                    
                    {/* 메시지 내용 */}
                    <div>
                        <div style={{
                            fontSize: '16px',
                            fontWeight: '700',
                            color: '#1e293b',
                            marginBottom: '12px'
                        }}>
                            메시지 내용
                        </div>
                        <div style={{
                            padding: '20px',
                            backgroundColor: '#fffbeb',
                            border: '1px solid #fde68a',
                            borderRadius: '8px',
                            fontSize: '15px',
                            lineHeight: '1.8',
                            color: '#1e293b',
                            whiteSpace: 'pre-wrap',
                            minHeight: '200px'
                        }}>
                            {(order.emailSubject || order.emailInfo?.emailSubject) && `제목: ${order.emailSubject || order.emailInfo?.emailSubject}\n\n`}
                            {order.messageContent || '내용이 없습니다.'}
                        </div>
                    </div>
                    
                    {/* 인쇄 버튼 */}
                    <div className="no-print" style={{
                        marginTop: '32px',
                        display: 'flex',
                        justifyContent: 'flex-end'
                    }}>
                        <button
                            onClick={() => {
                                const targetId = user?.companyId || user?.uid;
                                const updateData = {
                                    printedAt: serverTimestamp(),
                                    printedBy: user?.uid || null
                                };
                                if (targetId === order.toUserId && order.status === 'pending') {
                                    updateData.status = 'in_progress';
                                }
                                updateDoc(doc(db, 'workOrders', orderId), updateData)
                                    .catch(error => console.error('인쇄 기록 실패:', error));
                                window.print();
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '12px 20px',
                                backgroundColor: '#6366f1',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '15px',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            <Printer size={18} /> 인쇄하기
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

    const prosthesisList = getGroupedProsthesisInfo();
    const targetId = user?.companyId || user?.uid;
    const isMyOrder = targetId === order.fromUserId;
    // 회신 역할 판정 (2026-08-25 P1)
    const myBt = user?.businessType || user?.companyBusinessType;
    const isLabAccount = myBt === 'lab' || myBt === '기공소';
    const isLabReceiver = targetId === order.toUserId && isLabAccount;
    // 도착 처리 표기 (2026-09-29 — 버튼 없음): 발신 치과 화면에서 자동 완료(auto_due) / 과거 수동 기록(receivedAt)만 작은 글씨로
    const arrivalNote = (isMyOrder && !isLabAccount)
        ? (order.stageSource === 'auto_due' ? '도착 예정일 지남(자동)' : (order.receivedAt ? '도착 확인됨(수동)' : null))
        : null;

    // 회신 유형·프리셋 (대표 확정 D4: 수주 거절 = 주문 종결)
    const REPLY_TYPES = [
        ['reject', '수주 거절'],
        ['question', '정보 문의'],
        ['revision', '수정 요청'],
    ];
    const REPLY_PRESETS = ['쉐이드 재확인 필요', '치식 확인 필요', '스캔 데이터 재전송 요청'];
    const replyTypeLabel = (t) => (REPLY_TYPES.find(([k]) => k === t) || [null, t])[1];

    // (구) 치과 도착 확인 버튼(handleConfirmReceipt, 2026-08-25 P1) — 2026-09-29 무접촉 공정 흐름(버튼 0개)으로 제거. 완료는 서버 자동(auto_due).

    // 회신 저장 — replies arrayUnion + 거절이면 status rejected(주문 종결, 대표 확정 D4) + 치과 알림(환자명 마스킹)
    const submitReply = async () => {
        if (replySending) return;
        const text = replyText.trim();
        if (!replyPreset && !text) { alert('프리셋을 고르거나 내용을 입력해 주세요.'); return; }
        if (replyType === 'reject' &&
            !window.confirm('수주를 거절하면 이 주문은 종결 처리되고 치과에 안내됩니다. 계속할까요?')) return;
        setReplySending(true);
        try {
            const reply = {
                type: replyType,
                preset: replyPreset || '',
                text,
                by: user?.uid || '',
                byName: user?.businessName || user?.companyName || user?.name || '',
                at: new Date().toISOString(),
            };
            const patch = { replies: arrayUnion(reply), updatedAt: serverTimestamp() };
            if (replyType === 'reject') patch.status = 'rejected';
            await updateDoc(doc(db, 'workOrders', order.id), patch);
            // 치과 알림 — 제목·본문에 환자 실명 대신 마스킹 이름(김**)만 (회사 규정: 푸시에 환자 식별정보 금지)
            // agent_/email_ 유령 ID에는 보내지 않는다 (알림 규정)
            const fromId = order.fromUserId || '';
            if (fromId && !fromId.startsWith('agent_') && !fromId.startsWith('email_')) {
                await createNotification({
                    recipientId: fromId,
                    type: 'order_reply',
                    title: `기공소 회신 — ${replyTypeLabel(replyType)}`,
                    message: `${maskName(order.patientName || '') || '해당'} 건: ${replyPreset || (text ? text.slice(0, 60) : replyTypeLabel(replyType))}`,
                    link: `/view-order/${order.id}`,
                    metadata: { orderId: order.id, replyType },
                });
            }
            setOrder(prev => (prev ? {
                ...prev,
                replies: [...(Array.isArray(prev.replies) ? prev.replies : []), reply],
                ...(replyType === 'reject' ? { status: 'rejected' } : {}),
            } : prev));
            setReplyOpen(false);
            setReplyPreset('');
            setReplyText('');
        } catch (err) {
            console.error('회신 저장 실패:', order.id, err);
            alert('회신 저장에 실패했습니다: ' + (err?.message || '잠시 후 다시 시도해 주세요.'));
        } finally {
            setReplySending(false);
        }
    };
    const attachmentCount = getAttachmentCount();
    const isEmailParsed = ['dentweb', 'email', 'r2cad'].includes(order.source);
    const pdfUrl = order.attachments?.find(a => a.type === 'pdf')?.url;
    // hideClusterTab 제거 — 항상 3탭 표시

    // [리메이크] — 이 의뢰서를 불러와 재제작/수리 의뢰서를 만든다 (ClinicToday goRemake와 같은 규약, 2026-09-04)
    // 주문 문서를 통째로 싣지 않는다 — 필요한 값만 (환자 정보가 브라우저 기록에 남지 않게)
    const goRemakeWithKind = (o, remakeKind) => {
        setRemakePick(null);
        const pick = {
            id: o.id, patientName: o.patientName || '', patientGender: o.patientGender || '',
            patientAge: o.patientAge || '', toUserId: o.toUserId || '', isInternalLab: !!o.isInternalLab,
            items: o.items || [], prosthesisGroups: o.prosthesisGroups || [], bridgeGroups: o.bridgeGroups || [],
            ponticGroups: o.ponticGroups || [], implantGroups: o.implantGroups || [],
            specialNotes: o.specialNotes || '',
            createdAt: o.createdAt?.toDate ? o.createdAt.toDate().toISOString() : (o.createdAt || null),
            isRemake: !!o.isRemake, remakeOfOrderId: o.remakeOfOrderId || '',
            remakeKind,   // 'remake' | 'repair'
        };
        navigate('/create-order', { state: { remakeFrom: pick } });
    };

    const handleEdit = () => {
        // 필기 주문은 필기 수정 화면으로 (기존 필기 위에 덧쓰기)
        if (order.orderType === 'handwritten') {
            navigate(`/handwritten-order?editOrderId=${order.id}`);
            return;
        }
        // Timestamp를 안전하게 직렬화 가능한 형태로 변환
        const serializableOrder = {
            ...order,
            // Timestamp 필드들을 ISO 문자열 또는 밀리초로 변환
            dueDate: order.dueDate?.toDate ? order.dueDate.toDate().toISOString() : order.dueDate,
            createdAt: order.createdAt?.toDate ? order.createdAt.toDate().toISOString() : order.createdAt,
            updatedAt: order.updatedAt?.toDate ? order.updatedAt.toDate().toISOString() : order.updatedAt,
            completionDate: order.completionDate?.toDate ? order.completionDate.toDate().toISOString() : order.completionDate,
            readAt: order.readAt?.toDate ? order.readAt.toDate().toISOString() : order.readAt,
            printedAt: order.printedAt?.toDate ? order.printedAt.toDate().toISOString() : order.printedAt,
        };

        navigate('/create-order', {
            state: { editMode: true, orderId: order.id, orderData: serializableOrder }
        });
    };

    return (
        <>
            <RemakeKindModal order={remakePick} onPick={(k) => goRemakeWithKind(remakePick, k)} onClose={() => setRemakePick(null)} />
            <style>
                {`@media print {
                    html, body {
                        margin: 0 !important;
                        padding: 0 !important;
                        background: white !important;
                    }
                    body * { visibility: hidden; }
                    .print-area, .print-area * { visibility: visible; }
                    .print-area {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100% !important;
                        padding: 2mm 5mm !important;
                        box-shadow: none !important;
                    }
                    .print-area h1 {
                        font-size: 19px !important;
                        margin-bottom: 12px !important;
                    }
                    .print-spacer { display: none !important; }
                    .no-print { display: none !important; }
                    * {
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                }
                @page { size: A4; margin: 8mm; }`}
            </style>
            
            <div style={styles.container} className="view-order-container">
                <div className="no-print">
                    <PageHeader
                        icon={FileText}
                        title="주문 상세"
                        actions={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <button onClick={handleBack} style={styles.backBtn}>
                                    <ArrowLeft size={18} /> 뒤로가기
                                </button>
                                {isMyOrder && (
                                    <button onClick={handleEdit} style={styles.editBtn}>
                                        <Edit2 size={18} /> 수정
                                    </button>
                                )}
                                {/* 도착 처리 표기 (2026-09-29 — 버튼 없음): 자동 완료 '도착 예정일 지남(자동)' / 과거 수동 기록 '도착 확인됨(수동)' */}
                                {arrivalNote && (
                                    <span title="자동 처리는 편의 표시이며 납품·수령을 증명하지 않습니다. 확정은 거래명세서 확인에 따릅니다."
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '8px 12px', border: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: 8, color: '#64748b', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                        {arrivalNote}
                                    </span>
                                )}
                                {/* [리메이크] 재제작/수리 (2026-09-04) — 발신 치과, 필기 의뢰서 제외 */}
                                {isMyOrder && !isLabAccount && order.orderType !== 'handwritten' && (
                                    <button
                                        onClick={() => setRemakePick(order)}
                                        title="이 의뢰서를 불러와 재제작 또는 수리 의뢰서를 만듭니다"
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '8px 12px', border: '1px solid #fecaca', background: '#fef2f2', borderRadius: 8, cursor: 'pointer', color: '#b91c1c', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                        {'리메이크'}
                                    </button>
                                )}
                                {/* 기공소 → 치과 회신 (2026-08-25 P1) */}
                                {isLabReceiver && (
                                    <button
                                        onClick={() => setReplyOpen(true)}
                                        title="수주 거절·정보 문의·수정 요청을 치과에 남깁니다"
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '8px 12px', border: '1px solid #C7D2FE', background: '#EEF2FF', borderRadius: 8, cursor: 'pointer', color: '#4338CA', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                        {'✉ 치과에 회신'}
                                    </button>
                                )}
                                {order.designStatus && (
                                    <button
                                        onClick={() => navigate(`/design-review/${order.id}`)}
                                        style={{
                                            ...styles.editBtn,
                                            backgroundColor: order.designStatus === 'review' ? '#6366f1' : '#f8fafc',
                                            color: order.designStatus === 'review' ? '#fff' : '#6366f1',
                                            border: '1px solid #6366f1',
                                        }}
                                    >
                                        <FileText size={18} />
                                        {order.designStatus === 'review' ? '디자인 확인' : '디자인 상태'}
                                    </button>
                                )}
                            </div>
                        }
                    />
                </div>

                {/* 🩺 장착 후 확인 — 발신 치과 화면, 환자 명부에 연결된 의뢰서만(카드가 플래그·연결 여부 판정). 인쇄 제외(no-print) */}
                {isMyOrder && !isLabAccount && <OrderFeedbackCard order={order} user={user} />}
                {isMyOrder && !isLabAccount && <OrderReceptionLink order={order} />}
                {isMyOrder && !isLabAccount && <OrderPatientLink order={order} user={user} />}
                
                <div style={styles.tabsContainer} className="no-print">
                    <button
                        style={{
                            ...styles.tab,
                            ...(activeTab === 'cluster' ? styles.tabActive : {})
                        }}
                        onClick={() => setActiveTab('cluster')}
                    >
                        <FileText size={18} /> 덴탈클러스터의뢰서
                    </button>
                    {pdfUrl && (
                        <button
                            style={{
                                ...styles.tab,
                                ...(activeTab === 'original' ? styles.tabActive : {})
                            }}
                            onClick={() => {
                                setActiveTab('original');
                                setNumPages(null);
                                setLoadedPages(1);
                                setPdfError(false);
                            }}
                        >
                            <FileText size={18} /> 의뢰서
                        </button>
                    )}
                    <button
                        style={{
                            ...styles.tab,
                            ...(activeTab === 'attachments' ? styles.tabActive : {})
                        }}
                        onClick={() => setActiveTab('attachments')}
                    >
                        <Paperclip size={18} /> 첨부파일
                        {attachmentCount > 0 && (
                            <span style={styles.badge}>{attachmentCount}</span>
                        )}
                    </button>
                </div>

                {/* ── 회신 내역 타임라인 (2026-08-25 P1) — 치과·기공소 모두 표시, 인쇄 제외 ── */}
                {order.status === 'rejected' && (
                    <div className="no-print" style={{ margin: '12px 0', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', fontSize: 13, fontWeight: 700, color: '#DC2626' }}>
                        {'이 주문은 기공소가 수주를 거절하여 종결되었습니다. 아래 회신 내용을 확인해 주세요.'}
                    </div>
                )}
                {Array.isArray(order.replies) && order.replies.length > 0 && (
                    <div className="no-print" style={{ margin: '12px 0', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px' }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
                            {`✉ 기공소 회신 ${order.replies.length}건`}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {[...order.replies]
                                .sort((a, b) => String(a?.at || '').localeCompare(String(b?.at || '')))
                                .map((r, i) => {
                                    const d = r?.at ? new Date(r.at) : null;
                                    const when = d && !isNaN(d.getTime())
                                        ? `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
                                        : '';
                                    const isReject = r?.type === 'reject';
                                    return (
                                        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, lineHeight: 1.6 }}>
                                            <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, color: isReject ? '#DC2626' : '#4338CA', background: isReject ? '#FEF2F2' : '#EEF2FF', border: `1px solid ${isReject ? '#FECACA' : '#C7D2FE'}` }}>
                                                {replyTypeLabel(r?.type)}
                                            </span>
                                            <span style={{ color: '#334155' }}>
                                                {[r?.preset, r?.text].filter(Boolean).join(' — ') || '(내용 없음)'}
                                            </span>
                                            <span style={{ marginLeft: 'auto', flexShrink: 0, fontSize: 11.5, color: '#94a3b8' }}>
                                                {[r?.byName, when].filter(Boolean).join(' · ')}
                                            </span>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                )}

                {/* ── 회신 작성 모달 (수신 기공소 전용) ── */}
                {replyOpen && (
                    <div className="no-print" style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
                        onClick={() => !replySending && setReplyOpen(false)}>
                        <div style={{ background: '#fff', borderRadius: 12, padding: '20px 22px', width: '100%', maxWidth: 440, boxShadow: '0 10px 30px rgba(15,23,42,0.2)' }}
                            onClick={(e) => e.stopPropagation()}>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{'치과에 회신'}</div>
                            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 14 }}>
                                {'회신은 이 의뢰서에 기록되고 치과에 알림이 갑니다.'}
                            </div>
                            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                                {REPLY_TYPES.map(([k, lb]) => (
                                    <button key={k} onClick={() => setReplyType(k)}
                                        style={{ flex: 1, padding: '8px 4px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', border: `1px solid ${replyType === k ? (k === 'reject' ? '#FCA5A5' : '#6366f1') : '#e2e8f0'}`, background: replyType === k ? (k === 'reject' ? '#FEF2F2' : '#EEF2FF') : '#fff', color: replyType === k ? (k === 'reject' ? '#DC2626' : '#4338CA') : '#64748b' }}>
                                        {lb}
                                    </button>
                                ))}
                            </div>
                            {replyType === 'reject' && (
                                <div style={{ fontSize: 12, color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 10px', marginBottom: 12 }}>
                                    {'수주 거절을 보내면 이 주문은 종결 처리됩니다.'}
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                                {REPLY_PRESETS.map(p => (
                                    <button key={p} onClick={() => setReplyPreset(prev => (prev === p ? '' : p))}
                                        style={{ padding: '5px 10px', borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: `1px solid ${replyPreset === p ? '#6366f1' : '#e2e8f0'}`, background: replyPreset === p ? '#EEF2FF' : '#f8fafc', color: replyPreset === p ? '#4338CA' : '#64748b' }}>
                                        {p}
                                    </button>
                                ))}
                            </div>
                            <textarea
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder="직접 쓰기 (선택) — 프리셋만 골라 보내도 됩니다"
                                rows={3}
                                style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 13, color: '#334155', fontFamily: 'inherit', resize: 'vertical', outline: 'none' }}
                            />
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
                                <button onClick={() => setReplyOpen(false)} disabled={replySending}
                                    style={{ padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: '1px solid #e2e8f0', background: '#fff', color: '#64748b' }}>
                                    {'취소'}
                                </button>
                                <button onClick={submitReply} disabled={replySending}
                                    style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: replySending ? 'default' : 'pointer', fontFamily: 'inherit', border: 'none', background: replyType === 'reject' ? '#DC2626' : '#6366f1', color: '#fff', opacity: replySending ? 0.6 : 1 }}>
                                    {replySending ? '보내는 중…' : (replyType === 'reject' ? '거절 보내기' : '회신 보내기')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div style={styles.tabContent}>
                    {activeTab === 'original' && (
                        <div style={{ padding: '20px', minHeight: '820px', position: 'relative' }}>
                            {/* 원본의뢰서 PDF/인쇄 버튼 */}
                            <div className="no-print" style={{ 
                                display: 'flex', 
                                gap: '10px', 
                                marginBottom: '16px',
                                justifyContent: 'flex-end'
                            }}>
                                <button 
                                    onClick={async () => {
                                        const pdfFile = order.attachments?.find(a => a.type === 'pdf');
                                        if (pdfFile) {
                                            try { 
                                                await updateDoc(doc(db, 'workOrders', orderId), { 
                                                    downloadedPdfAt: serverTimestamp(), 
                                                    downloadedPdfBy: user?.uid || null 
                                                }); 
                                            } catch(e) { 
                                                console.error('다운로드 기록 실패:', e); 
                                            }
                                            await handleFileDownload(
                                                pdfFile.url, 
                                                (order.patientName || '환자') + '_기공의뢰서_원본.pdf'
                                            );
                                        }
                                    }} 
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '10px 16px',
                                        backgroundColor: '#10b981',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '15px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <Download size={18} /> PDF 다운로드
                                </button>
                                <button
                                    onClick={async () => {
                                        const pdfFile = order.attachments?.find(a => a.type === 'pdf');
                                        if (!pdfFile) return;

                                        // 인쇄 기록 - 비동기로 처리 (인쇄 다이얼로그 지연 방지)
                                        const targetId = user?.companyId || user?.uid;
                                        const updateData = {
                                            printedAt: serverTimestamp(),
                                            printedBy: user?.uid || null
                                        };
                                        if (targetId === order.toUserId && order.status === 'pending') {
                                            updateData.status = 'in_progress';
                                        }
                                        updateDoc(doc(db, 'workOrders', orderId), updateData)
                                            .catch(error => console.error('인쇄 기록 실패:', error));
                                        markOrderReceivedStage(order, user);   // 인쇄 실행 = 제작 현황 '의뢰서확인' 진입

                                        try {
                                            const pathMatch = pdfFile.url.match(/\/o\/(.+?)\?/);
                                            if (pathMatch) {
                                                const storagePath = decodeURIComponent(pathMatch[1]);
                                                const fileRef = ref(storage, storagePath);
                                                const blob = await getBlob(fileRef);
                                                const blobUrl = window.URL.createObjectURL(blob);
                                                
                                                const iframe = document.createElement('iframe');
                                                iframe.style.display = 'none';
                                                iframe.src = blobUrl;
                                                document.body.appendChild(iframe);
                                                
                                                iframe.onload = () => {
                                                    setTimeout(() => {
                                                        iframe.contentWindow.print();
                                                        
                                                        // 인쇄 완료 또는 취소 시 iframe 제거
                                                        const cleanup = () => {
                                                            try {
                                                                document.body.removeChild(iframe);
                                                                window.URL.revokeObjectURL(blobUrl);
                                                            } catch (e) {
                                                                console.log('cleanup error:', e);
                                                            }
                                                        };
                                                        
                                                        // 인쇄 후 이벤트 리스너
                                                        iframe.contentWindow.onafterprint = cleanup;
                                                        // fallback: 60초 후 자동 제거
                                                        setTimeout(cleanup, 60000);
                                                    }, 500);
                                                };
                                            } else {
                                                window.open(pdfFile.url, '_blank')?.print();
                                            }
                                        } catch (error) {
                                            console.error('PDF 인쇄 실패:', error);
                                            window.open(pdfFile.url, '_blank')?.print();
                                        }
                                    }} 
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '10px 16px',
                                        backgroundColor: '#6366f1',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '15px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <Printer size={18} /> 인쇄하기
                                </button>
                            </div>
                            
                            {pdfUrl ? (
                                <>
                                    {!numPages && !pdfError && (
                                        <div style={{ 
                                            position: 'absolute', 
                                            top: '50%', 
                                            left: '50%', 
                                            transform: 'translate(-50%, -50%)', 
                                            textAlign: 'center', 
                                            zIndex: 10 
                                        }}>
                                            <div style={styles.spinner}></div>
                                            <p style={{ color: '#64748b', marginTop: '12px' }}>
                                                PDF 로딩 중...
                                            </p>
                                        </div>
                                    )}
                                    
                                    {pdfError && (
                                        <div style={{ 
                                            textAlign: 'center', 
                                            padding: '60px', 
                                            color: '#ef4444' 
                                        }}>
                                            <p>PDF를 불러올 수 없습니다.</p>
                                            <button 
                                                onClick={() => {
                                                    setPdfError(false);
                                                    setNumPages(null);
                                                    setLoadedPages(1);
                                                }}
                                                style={{
                                                    marginTop: '12px',
                                                    padding: '8px 16px',
                                                    backgroundColor: '#6366f1',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                다시 시도
                                            </button>
                                        </div>
                                    )}
                                    
                                    <div style={{ 
                                        maxHeight: '800px', 
                                        overflowY: 'auto', 
                                        opacity: numPages ? 1 : 0, 
                                        transition: 'opacity 0.3s' 
                                    }}>
                                        <Document 
                                            file={pdfUrl} 
                                            onLoadSuccess={handlePdfLoadSuccess}
                                            onLoadError={handlePdfLoadError}
                                            loading={null}
                                            error={null}
                                            options={pdfOptions}
                                        >
                                            {Array.from(new Array(loadedPages), (el, index) => (
                                                <Page 
                                                    key={index + 1} 
                                                    pageNumber={index + 1} 
                                                    width={700} 
                                                    renderTextLayer={false} 
                                                    renderAnnotationLayer={false}
                                                    loading={
                                                        <div style={{ 
                                                            padding: '20px', 
                                                            textAlign: 'center', 
                                                            color: '#94a3b8',
                                                            minHeight: '100px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}>
                                                            <div style={styles.spinner}></div>
                                                        </div>
                                                    }
                                                />
                                            ))}
                                        </Document>
                                        
                                        {numPages && loadedPages < numPages && (
                                            <div style={{ 
                                                textAlign: 'center', 
                                                padding: '20px', 
                                                color: '#64748b' 
                                            }}>
                                                <div style={styles.spinner}></div>
                                                <p style={{ marginTop: '8px', fontSize: '14px' }}>
                                                    {loadedPages} / {numPages} 페이지 로딩 중...
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div style={{ 
                                    textAlign: 'center', 
                                    padding: '60px', 
                                    color: '#94a3b8' 
                                }}>
                                    PDF 파일이 없습니다.
                                </div>
                            )}
                        </div>
                    )}
                    
                    {activeTab === 'attachments' && renderAttachmentsTab()}
                    
                    {activeTab === 'cluster' && (
                        <>
                            {/* 클러스터 의뢰서 PDF/인쇄 버튼 */}
                            <div className="no-print" style={{ 
                                display: 'flex', 
                                gap: '10px', 
                                padding: '20px 20px 0',
                                justifyContent: 'flex-end'
                            }}>
                                <button 
                                    onClick={handleDownloadPDF} 
                                    disabled={pdfLoading}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '10px 16px',
                                        backgroundColor: pdfLoading ? '#94a3b8' : '#10b981',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '15px',
                                        fontWeight: '600',
                                        cursor: pdfLoading ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    <Download size={18} /> {pdfLoading ? '생성중...' : 'PDF 다운로드'}
                                </button>
                                <button
                                    onClick={() => {
                                        // 인쇄 기록 - 비동기로 처리 (인쇄 다이얼로그 지연 방지)
                                        const targetId = user?.companyId || user?.uid;
                                        const updateData = {
                                            printedAt: serverTimestamp(),
                                            printedBy: user?.uid || null
                                        };
                                        if (targetId === order.toUserId && order.status === 'pending') {
                                            updateData.status = 'in_progress';
                                        }
                                        updateDoc(doc(db, 'workOrders', orderId), updateData)
                                            .catch(error => console.error('인쇄 기록 실패:', error));
                                        markOrderReceivedStage(order, user);   // 인쇄 실행 = 제작 현황 '의뢰서확인' 진입
                                        window.print();
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '10px 16px',
                                        backgroundColor: '#6366f1',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '15px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <Printer size={18} /> 인쇄하기
                                </button>
                                <button
                                    onClick={() => setShowLabelModal(true)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '10px 16px',
                                        backgroundColor: '#f59e0b',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '15px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    🏷️ 라벨 인쇄
                                </button>
                            </div>

                            <div ref={printRef} style={styles.orderSheet} className="print-area">
                            {/* 필기 주문: 필기 원본만 표시 (구조화 양식 숨김) */}
                            {order.orderType === 'handwritten' && order.handwrittenImageUrl ? (
                                <div>
                                    {order.handwrittenHistory?.length > 0 && (
                                        <div className="no-print" style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 6,
                                            fontSize: 12, fontWeight: 700, color: '#ea580c',
                                            backgroundColor: '#fff7ed', border: '1px solid #fed7aa',
                                            borderRadius: 6, padding: '4px 10px', marginBottom: 8,
                                        }}>
                                            ✍️ 수정된 필기 의뢰서 (최종본) · 이전 버전 {order.handwrittenHistory.length}건 아래 참조
                                        </div>
                                    )}
                                    <img
                                        src={order.handwrittenImageUrl}
                                        alt="필기 의뢰서"
                                        style={{ width: '100%', display: 'block', cursor: 'zoom-in' }}
                                        onClick={() => setPreviewImage({ url: order.handwrittenImageUrl, filename: '필기 의뢰서' })}
                                    />
                                    {/* 이전 필기 이력 (인쇄 제외) */}
                                    {order.handwrittenHistory?.length > 0 && (
                                        <div className="no-print" style={{ marginTop: 14 }}>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: '#64748b', marginBottom: 8 }}>
                                                이전 필기 (수정 전) — 클릭하면 크게 보기
                                            </div>
                                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                                {order.handwrittenHistory.map((h, i) => (
                                                    <div key={i} style={{ textAlign: 'center' }}>
                                                        <img
                                                            src={h.url}
                                                            alt={`이전 필기 ${i + 1}차`}
                                                            style={{
                                                                width: 110, height: 155, objectFit: 'cover', objectPosition: 'top',
                                                                border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'zoom-in',
                                                                opacity: 0.85, filter: 'grayscale(25%)',
                                                            }}
                                                            onClick={() => setPreviewImage({ url: h.url, filename: `이전 필기 (${i + 1}차)` })}
                                                        />
                                                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                                                            {i + 1}차 작성
                                                            {h.replacedAt && ` · ~${(h.replacedAt.toDate ? h.replacedAt.toDate() : new Date(h.replacedAt)).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' })}`}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (<>
                            {/* 상단 헤더: 좌측 기공소 / 우측 차트번호+환자+의사+날짜 */}
                            <div style={styles.pdfTopHeader}>
                                <div style={styles.pdfTopLeft}>
                                    {receiverName}
                                </div>
                                <div style={styles.pdfTopRight}>
                                    <span>{order.chartNumber || ''} {order.patientName || '-'} / Dr.{senderInfo?.doctorName || senderInfo?.name || '-'}</span>
                                    <span>{formatShortDate(order.createdAt)}</span>
                                </div>
                            </div>

                            {/* 중앙 타이틀 */}
                            <h1 style={styles.pdfTitle}>
                                {senderName} 기공물 제작 의뢰서
                                {order.isRemake && (
                                    <span style={{ fontSize: '16px', fontWeight: '400' }}> [{remakeLabelOf(order)}]</span>
                                )}
                            </h1>

                            {/* QR 발급 실패 안내 (인쇄에는 안 찍힘) — 조용한 실패 금지 */}
                            {qrToken === 'failed' && (
                                <div className="no-print" style={{ fontSize: 11, color: '#B45309', margin: '2px 0 4px' }}>
                                    {'공정 체크 QR을 만들지 못해 QR 없이 인쇄됩니다. 새로 고침하면 다시 시도합니다.'}
                                </div>
                            )}
                            {/* 정보 테이블: 의뢰기공소/납품요구일 + 환자정보 */}
                            <table style={styles.pdfInfoTable}>
                                <colgroup>
                                    {/* QR 칸(약 22mm)을 오른쪽에 추가 — 기존 5열 폭을 조금씩 줄임 (QR 공정체크 §5-4) */}
                                    <col style={{ width: '8%' }} />
                                    <col style={{ width: '10%' }} />
                                    <col style={{ width: '27%' }} />
                                    <col style={{ width: '12%' }} />
                                    <col style={{ width: (qrToken && qrToken !== 'failed') ? '31%' : '43%' }} />
                                    {qrToken && qrToken !== 'failed' && <col style={{ width: '12%' }} />}
                                </colgroup>
                                <tbody>
                                    <tr>
                                        <td style={styles.pdfLabelCell} colSpan="2">의뢰 기공소</td>
                                        <td style={styles.pdfValueCell}>{receiverName}</td>
                                        <td style={styles.pdfLabelCell}>납품요구일</td>
                                        <td style={styles.pdfValueCell}>
                                            {(() => {
                                                const d = order.dueDate || order.completionDate;
                                                if (!d) return '-';
                                                const date = d.toDate ? d.toDate() : d instanceof Date ? d : new Date(d);
                                                if (isNaN(date.getTime())) return '-';
                                                const h = date.getHours();
                                                const m = date.getMinutes();
                                                const timeStr = (h > 0 || m > 0) ? ` ${String(h).padStart(2,'0')}시` : '';
                                                return `${date.getFullYear()}년 ${String(date.getMonth()+1).padStart(2,'0')}월 ${String(date.getDate()).padStart(2,'0')}일${timeStr}`;
                                            })()}
                                            {(() => {
                                                // 환자 내원일 표시 (2026-08-25 P2 — 내원일 입력 부활 D2에 맞춰 상세·인쇄에도 노출.
                                                // 마감 계산은 caseChecks.getDeadline이 이미 내원일 우선 — 여기는 표시만)
                                                const v = order.visitDate;
                                                if (!v) return null;
                                                const vd = v.toDate ? v.toDate() : v instanceof Date ? v : new Date(v);
                                                if (isNaN(vd.getTime())) return null;
                                                return ` (내원 ${String(vd.getMonth() + 1).padStart(2, '0')}월 ${String(vd.getDate()).padStart(2, '0')}일)`;
                                            })()}
                                        </td>
                                        {/* QR 공정 체크 — 표 3줄 높이 전부 사용. 환자명은 표 안에 이미 있으므로 다시 쓰지 않는다 */}
                                        {qrToken && qrToken !== 'failed' && (
                                            <td rowSpan="3" style={{ ...styles.pdfValueCell, textAlign: 'center', verticalAlign: 'middle', padding: '4px' }}>
                                                <QRCodeSVG value={tokenUrl(qrToken)} size={74} level="M" />
                                                <div style={{ fontSize: '8px', color: '#000', marginTop: '2px' }}>덴클</div>
                                            </td>
                                        )}
                                    </tr>
                                    <tr>
                                        <td style={styles.pdfLabelCell} rowSpan="2">환자{'\n'}정보</td>
                                        <td style={styles.pdfSubLabelCell}>성명</td>
                                        <td style={styles.pdfPatientValueCell}>{order.patientName || '-'}</td>
                                        <td style={styles.pdfLabelCell}>차트번호</td>
                                        <td style={styles.pdfPatientValueCell}>{order.chartNumber || '-'}</td>
                                    </tr>
                                    <tr>
                                        <td style={styles.pdfSubLabelCell}>나이/성별</td>
                                        <td style={styles.pdfPatientValueCell}>
                                            {order.patientAge ? `만 ${order.patientAge}세` : '-'}
                                            {order.patientGender ? ` / ${order.patientGender === 'M' ? '남자' : order.patientGender === 'F' ? '여자' : order.patientGender}` : ''}
                                        </td>
                                        <td style={styles.pdfLabelCell}>성별</td>
                                        <td style={styles.pdfPatientValueCell}>
                                            {order.patientGender === 'M' ? '남자' : order.patientGender === 'F' ? '여자' : order.patientGender || '-'}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            {/* 메인 컨텐츠: 처방 내용 */}
                            <div style={styles.pdfContentBox}>
                                {/* 치식도 + 보철물 렌더링 (덴트웹/이메일은 특이사항만 표시) */}
                                {(() => {
                                    const isDentweb = ['dentweb', 'email', 'r2cad'].includes(order.source);
                                    const hasStructuredData = !isDentweb && (prosthesisList.length > 0 || (order.items && order.items.length > 0));
                                    return hasStructuredData ? (
                                    <>
                                        {/* 상단: 치식도 + 쉐이드 (한 줄, 전체 너비) */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 16px', borderBottom: '1px solid #000' }}>
                                            <div style={{ flexShrink: 0 }}>
                                                {renderToothChart()}
                                            </div>
                                            {getShadeText() !== '-' && (
                                                <div style={{ fontSize: '13px', color: '#000' }}>
                                                    <span style={{ fontWeight: '700' }}>&lt;쉐이드&gt;</span> {getShadeText()}{getShadeTypeText() ? ` (${getShadeTypeText()})` : ''}
                                                </div>
                                            )}
                                        </div>

                                        {/* 하단: 보철물 정보 목록 */}
                                        <div style={{ padding: '0' }}>
                                        {(() => {
                                            // 11-02 대표: 종류마다 한 줄 — <지르코니아 크라운> #12 / <임플란트 Osstem TS Mini> #14 / <폰틱> #12 / <브릿지> #12-#14, #15-#17
                                            const lines = orderKindLines(order);
                                            return lines.length > 0 ? lines.map((ln, i) => (
                                                <div key={i} style={{
                                                    padding: '6px 16px',
                                                    borderBottom: i < lines.length - 1 ? '1px solid #ccc' : '1px solid #000',
                                                }}>
                                                    <div style={{ fontSize: '13px', lineHeight: '1.4', color: '#000' }}>
                                                        <strong>&lt;{ln.label}&gt;</strong>{' '}<span style={{ fontWeight: 700 }}>{ln.teeth}</span>
                                                    </div>
                                                </div>
                                            )) : (
                                                <div style={{ padding: '16px', color: '#999', fontSize: '14px', borderBottom: '1px solid #000' }}>
                                                    보철물 정보 없음
                                                </div>
                                            );
                                        })()}
                                        </div>

                                        {/* 특이사항 */}
                                        <div style={{ padding: '10px 16px' }}>
                                            <div style={{ fontSize: '13px', fontWeight: '700', color: '#000', marginBottom: '4px' }}>특이사항</div>
                                            <div style={{ fontSize: '14px', lineHeight: '1.8', color: '#000', whiteSpace: 'pre-line' }}>
                                                {cleanSpecialNotes(order.specialNotes || order.notes || order.memo)}
                                            </div>
                                            {order.isRemake && order.remakeReason && (
                                                <div style={{ marginTop: '8px', fontSize: '14px', color: '#000' }}>
                                                    [{remakeLabelOf(order)}]{order.remakeCause ? ` ${remakeCauseLabel(order.remakeCause)} ·` : ''} {order.remakeReason}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    /* 구조화 데이터 없는 경우: 텍스트 표시 */
                                    <div style={{ padding: '10px 16px' }}>
                                        <div style={{ fontSize: '14px', lineHeight: '1.8', color: '#000', whiteSpace: 'pre-line' }}>
                                            {cleanSpecialNotes(order.specialNotes || order.notes || order.memo)}
                                        </div>
                                        {order.isRemake && order.remakeReason && (
                                            <div style={{ marginTop: '8px', fontSize: '14px', color: '#000' }}>
                                                [{remakeLabelOf(order)}]{order.remakeCause ? ` ${remakeCauseLabel(order.remakeCause)} ·` : ''} {order.remakeReason}
                                            </div>
                                        )}
                                    </div>
                                );
                                })()}
                                {/* 빈 여백 */}
                                <div className="print-spacer" style={{ height: '40px' }}></div>
                            </div>

                            </>)}

                            {/* 디지털 워크플로우 (IVAN 연동) - 테이블 밖 · 필기주문에서도 표시 */}
                            {(order.digitalFiles?.length > 0 || order.designStatus || linkedScan) && (
                                <div style={{ margin: '12px 0', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#333', marginBottom: '8px' }}>디지털 워크플로우</div>
                                    {order.designStatus && (
                                        <div style={{
                                            display: 'flex', alignItems: 'center', gap: 8,
                                            padding: '6px 10px', borderRadius: 4, marginBottom: 8,
                                            backgroundColor: order.designStatus === 'completed' ? '#f0fdf4' :
                                                order.designStatus === 'review' ? '#fef3c7' : '#f5f3ff',
                                        }}>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>디자인 상태:</span>
                                            <span style={{
                                                fontSize: 11, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                                                color: '#fff',
                                                backgroundColor: order.designStatus === 'completed' ? '#10b981' :
                                                    order.designStatus === 'approved' ? '#3b82f6' :
                                                    order.designStatus === 'review' ? '#f59e0b' :
                                                    order.designStatus === 'milling' ? '#8b5cf6' : '#94a3b8',
                                            }}>
                                                {{ pending: '대기', in_progress: '디자인중', review: '확인요청',
                                                   approved: '승인', milling: '가공중', completed: '완료'
                                                }[order.designStatus] || order.designStatus}
                                            </span>
                                        </div>
                                    )}
                                    {order.digitalFiles?.length > 0 && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            {order.digitalFiles.map((file, i) => (
                                                <div key={i} style={{
                                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                    padding: '6px 10px', backgroundColor: '#f8fafc', borderRadius: 4,
                                                    border: '1px solid #e2e8f0',
                                                }}>
                                                    <span style={{ fontSize: 12, color: '#334155' }}>{file.fileName}</span>
                                                    <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                                        {file.fileUrl && /\.(stl|zip)$/i.test(file.fileName || '') && (
                                                            <button
                                                                onClick={() => setStlPreview({ url: file.fileUrl, fileName: file.fileName })}
                                                                style={{ fontSize: 11, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}
                                                            >
                                                                3D 미리보기
                                                            </button>
                                                        )}
                                                        {file.fileUrl && (
                                                            <a href={file.fileUrl} target="_blank" rel="noopener noreferrer"
                                                               onClick={() => markOrderReceivedStage(order, user)}
                                                               style={{ fontSize: 11, color: '#6366f1', textDecoration: 'none' }}>
                                                                다운로드
                                                            </a>
                                                        )}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {/* 통합 에이전트 스캔 파일 (경유-삭제 전까지 미리보기 가능) */}
                                    {linkedScan && (
                                        <div style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            padding: '6px 10px', backgroundColor: '#eef2ff', borderRadius: 4,
                                            border: '1px solid #c7d2fe', marginTop: 4,
                                        }}>
                                            <span style={{ fontSize: 12, color: '#3730a3' }}>
                                                스캔 데이터 ({linkedScan.caseLabel || linkedScan.id}
                                                {linkedScan.files?.length ? ` · 파일 ${linkedScan.files.length}개` : ''})
                                            </span>
                                            {/* 3D 미리보기·다운로드 제거(2026-07-25 사장 지시) — 스캔은 에이전트로 자동 수신 ([CAD로 보내기]는 2026-10-25 제거) */}
                                            <span style={{ fontSize: 11, color: '#94a3b8' }}>
                                                {linkedScan.status === 'detected' || linkedScan.status === 'uploading'
                                                    ? '치과 PC 전송 대기 중'
                                                    : linkedScan.archive?.downloadURL
                                                        ? '에이전트로 자동 수신됨'
                                                        : '기공소 수신 완료 (서버 파일 정리됨)'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* 디자인 미리보기 (2026-09-04 대표 확정 B안) — 기공소 에이전트가 올린 디자인 완료 3D 파일(designPreview.files).
                                치과·기공소 모두 열람. 완료 처리 시 서버가 파일·필드를 삭제한다. 앱은 P2(미구현). */}
                            {order.designPreview?.files?.length > 0 && (() => {
                                const files = order.designPreview.files.filter(f => f?.url);
                                const anyReduced = files.some(f => f.reduced);
                                const fmtSize = (n) => (!n && n !== 0) ? '' : n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))}KB` : `${(n / 1024 / 1024).toFixed(1)}MB`;
                                const upAt = order.designPreview.uploadedAt;
                                const upDate = upAt?.toDate ? upAt.toDate() : (upAt ? new Date(upAt) : null);
                                const upText = upDate && !isNaN(upDate) ? `${upDate.getMonth() + 1}/${upDate.getDate()} ${String(upDate.getHours()).padStart(2, '0')}:${String(upDate.getMinutes()).padStart(2, '0')}` : '';
                                const NOTICE = '미리보기는 디자인 확인용이며 최종 보철과 다를 수 있습니다.';
                                const KIND_LABEL = { crown: '크라운', bridge: '브릿지', inlay: '인레이', onlay: '온레이', veneer: '비니어', abutment: '어버트먼트', coping: '코핑', implant: '임플란트', denture: '덴처', splint: '스플린트', model: '모델' };
                                const kindOf = (k) => (k ? (KIND_LABEL[String(k).toLowerCase()] || k) : '');
                                const openAll = (startIdx = 0) => {
                                    if (files.length === 0) { alert('열 수 있는 파일이 없습니다.'); return; }
                                    const ordered = [...files.slice(startIdx), ...files.slice(0, startIdx)];
                                    setStlPreview({
                                        entries: ordered.map(f => ({ url: f.url, path: f.path, name: (f.path || '').split('/').pop(), tooth: f.tooth, kind: f.kind, format: f.format, size: f.size, reduced: !!f.reduced, layer: f.layer || null, part: f.part || null })),
                                        title: `디자인 미리보기 · ${order.patientName || ''}`,
                                        notice: NOTICE,
                                    });
                                };
                                return (
                                    <div className="no-print" style={{ margin: '12px 0', padding: '10px', border: '1px solid #c7d2fe', borderRadius: '4px', backgroundColor: '#f5f3ff' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: '13px', fontWeight: '700', color: '#3730a3' }}>디자인 미리보기</span>
                                            <span style={{ fontSize: 11, color: '#6366f1' }}>{files.length}개{upText ? ` · ${upText} 올림` : ''}</span>
                                            {anyReduced && <span style={{ fontSize: 10.5, fontWeight: 700, color: '#4338ca', background: '#e0e7ff', padding: '1px 6px', borderRadius: 4 }}>미리보기용 축소본</span>}
                                            <span style={{ flex: 1 }} />
                                            <button onClick={() => openAll(0)}
                                                style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: '#6366f1', border: 'none', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontFamily: 'inherit' }}>
                                                3D 보기
                                            </button>
                                        </div>
                                        {files.length === 0 && (
                                            <div style={{ fontSize: 12, color: '#b91c1c' }}>파일 주소가 비어 있어 열 수 없습니다. 기공소 PC의 덴클 프로그램이 다시 올려야 합니다.</div>
                                        )}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            {files.map((f, i) => (
                                                <div key={(f.path || f.url) + i} style={{
                                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                                                    padding: '6px 10px', backgroundColor: '#fff', borderRadius: 4, border: '1px solid #e0e7ff',
                                                }}>
                                                    <span style={{ fontSize: 12, color: '#334155', display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
                                                        {f.tooth && <b style={{ color: '#4338ca' }}>#{f.tooth}</b>}
                                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{kindOf(f.kind) || (f.path || '').split('/').pop() || `파일 ${i + 1}`}</span>
                                                        <span style={{ color: '#94a3b8' }}>{[fmtSize(f.size), f.format === 'glb-draco' ? 'GLB' : (f.format || '').toUpperCase(), f.reduced ? '축소본' : ''].filter(Boolean).join(' · ')}</span>
                                                    </span>
                                                    <button onClick={() => openAll(i)}
                                                        style={{ fontSize: 11, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                                                        보기
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 8, lineHeight: 1.5 }}>
                                            {NOTICE} 완료 처리 후에는 파일이 자동 삭제되어 더 이상 볼 수 없습니다.
                                        </div>
                                    </div>
                                );
                            })()}

                            {order.orderType !== 'handwritten' && (<>
                            {/* 법률 문구 + 서명 */}
                            <div style={styles.pdfLegalSection}>
                                <p style={styles.pdfLegalText}>
                                    의료기사 등에 관한 법률 제11조의3 및 같은 법 시행규칙 제12조의4 제1항에 따라 위와 같이 제작을 의뢰합니다.
                                </p>
                                <p style={styles.pdfDateText}>
                                    {(() => {
                                        const d = order.createdAt;
                                        if (!d) return '';
                                        const date = d.toDate ? d.toDate() : d instanceof Date ? d : new Date(d);
                                        if (isNaN(date.getTime())) return '';
                                        return `${date.getFullYear()}년 ${String(date.getMonth()+1).padStart(2,'0')}월 ${String(date.getDate()).padStart(2,'0')}일`;
                                    })()}
                                </p>
                                <div style={styles.pdfSignature}>
                                    <span>{senderName}</span>
                                    <span style={{ marginLeft: '16px' }}>{senderInfo?.doctorName || senderInfo?.name || ''}</span>
                                    {senderInfo?.sealImageUrl ? (
                                        <img src={senderInfo.sealImageUrl} alt="인감" style={styles.pdfStamp} />
                                    ) : (
                                        <div style={styles.pdfStampPlaceholder}>(인)</div>
                                    )}
                                </div>
                            </div>

                            {/* 하단 푸터: 주소/연락처 */}
                            <div style={styles.pdfFooterLine}>
                                {senderInfo?.address && <span>{senderInfo.address}</span>}
                                {senderInfo?.address && (senderInfo?.phone || senderInfo?.fax) && <span> </span>}
                                {senderInfo?.phone && <span>Tel:{senderInfo.phone}</span>}
                                {senderInfo?.fax && <span> Fax:{senderInfo.fax}</span>}
                            </div>
                            </>)}
                        </div>
                        </>
                    )}
                </div>
                
                {stlPreview && (
                    <StlViewer
                        url={stlPreview.url}
                        fileName={stlPreview.fileName}
                        entries={stlPreview.entries}
                        title={stlPreview.title}
                        notice={stlPreview.notice}
                        onClose={() => setStlPreview(null)}
                    />
                )}
                {showLabelModal && (
                    <LabelPrintModal
                        items={[buildLabelData(order, senderName)]}
                        onClose={() => setShowLabelModal(false)}
                        onPrinted={() => {
                            // 다른 인쇄와 동일하게 인쇄 기록 (수신자가 첫 인쇄 시 진행중(in_progress) 전환)
                            const updateData = {
                                printedAt: serverTimestamp(),
                                printedBy: user?.uid || null
                            };
                            if (targetId === order.toUserId && order.status === 'pending') {
                                updateData.status = 'in_progress';
                            }
                            updateDoc(doc(db, 'workOrders', orderId), updateData)
                                .catch(error => console.error('인쇄 기록 실패:', error));
                            markOrderReceivedStage(order, user);   // 라벨 인쇄 실행 = 제작 현황 '의뢰서확인' 진입
                        }}
                    />
                )}
                {previewImage && (
                    <div
                        style={styles.imageModal}
                        onClick={() => setPreviewImage(null)}
                    >
                        <div 
                            style={styles.modalContent} 
                            onClick={e => e.stopPropagation()}
                        >
                            <button 
                                style={styles.modalClose} 
                                onClick={() => setPreviewImage(null)}
                            >
                                <X size={24} />
                            </button>
                            <img 
                                src={previewImage.url} 
                                alt={previewImage.filename} 
                                style={styles.modalImage} 
                            />
                            <div style={styles.modalFooter}>
                                <span>{previewImage.filename || previewImage.name}</span>
                                <button 
                                    onClick={() => handleFileDownload(
                                        previewImage.url, 
                                        previewImage.filename || previewImage.name
                                    )} 
                                    style={styles.modalDownload}
                                >
                                    <Download size={16} /> 다운로드
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

const styles = {
    container: { 
        minHeight: '100vh', 
        backgroundColor: '#f1f5f9', 
        padding: '20px' 
    },
    actionBar: { 
        maxWidth: '210mm', 
        margin: '0 auto 16px', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: '10px 14px', 
        backgroundColor: '#6366f1', 
        borderRadius: '10px' 
    },
    backBtn: { 
        display: 'flex', 
        alignItems: 'center', 
        gap: '6px', 
        padding: '8px 16px', 
        backgroundColor: 'rgba(255,255,255,0.2)', 
        color: '#fff', 
        border: 'none', 
        borderRadius: '6px', 
        fontSize: '15px', 
        fontWeight: '600', 
        cursor: 'pointer' 
    },
    editBtn: { 
        display: 'flex', 
        alignItems: 'center', 
        gap: '6px', 
        padding: '8px 16px', 
        backgroundColor: '#f59e0b', 
        color: '#fff', 
        border: 'none', 
        borderRadius: '6px', 
        fontSize: '15px', 
        fontWeight: '600', 
        cursor: 'pointer' 
    },
    printMenuBtn: { 
        display: 'flex', 
        alignItems: 'center', 
        gap: '6px', 
        padding: '8px 16px', 
        backgroundColor: '#fff', 
        color: '#6366f1', 
        border: 'none', 
        borderRadius: '6px', 
        fontSize: '15px', 
        fontWeight: '600', 
        cursor: 'pointer' 
    },
    printDropdown: { 
        position: 'absolute', 
        top: '100%', 
        right: 0, 
        marginTop: '4px', 
        backgroundColor: '#fff', 
        borderRadius: '8px', 
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)', 
        overflow: 'hidden', 
        zIndex: 100, 
        minWidth: '160px' 
    },
    dropdownItem: { 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px', 
        width: '100%', 
        padding: '12px 16px', 
        backgroundColor: '#fff', 
        border: 'none', 
        fontSize: '14px', 
        fontWeight: '500', 
        color: '#374151', 
        cursor: 'pointer', 
        textAlign: 'left' 
    },
    tabsContainer: { 
        display: 'flex', 
        gap: '4px', 
        maxWidth: '210mm', 
        margin: '0 auto' 
    },
    tab: { 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px', 
        padding: '12px 24px', 
        backgroundColor: '#e2e8f0', 
        border: 'none', 
        borderRadius: '12px 12px 0 0', 
        fontSize: '16px', 
        fontWeight: '600', 
        color: '#64748b', 
        cursor: 'pointer' 
    },
    tabActive: { 
        backgroundColor: '#fff', 
        color: '#6366f1' 
    },
    badge: { 
        backgroundColor: '#6366f1', 
        color: '#fff', 
        padding: '2px 8px', 
        borderRadius: '10px', 
        fontSize: '14px', 
        fontWeight: '700', 
        marginLeft: '4px' 
    },
    tabContent: { 
        maxWidth: '210mm', 
        margin: '0 auto', 
        backgroundColor: '#fff', 
        borderRadius: '0 12px 12px 12px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
        minHeight: '400px' 
    },
    orderSheet: {
        padding: '20px 32px',
        fontFamily: "'Pretendard', 'Noto Sans KR', sans-serif",
    },
    pdfTopHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '16px',
    },
    pdfTopLeft: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#333',
    },
    pdfTopRight: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        fontSize: '13px',
        color: '#333',
        gap: '2px',
    },
    pdfTitle: {
        textAlign: 'center',
        fontSize: '22px',
        fontWeight: '700',
        color: '#000',
        margin: '0 0 24px',
        letterSpacing: '2px',
    },
    remakeBadge: {
        padding: '2px 8px',
        backgroundColor: '#fef2f2',
        color: '#dc2626',
        borderRadius: '4px',
        fontSize: '13px',
        fontWeight: '700',
    },
    pdfInfoTable: {
        width: '100%',
        borderCollapse: 'collapse',
        marginBottom: '0',
        border: '1px solid #000',
    },
    pdfLabelCell: {
        padding: '8px 12px',
        backgroundColor: '#f5f5f5',
        border: '1px solid #000',
        fontSize: '13px',
        fontWeight: '700',
        color: '#000',
        textAlign: 'center',
        whiteSpace: 'pre-line',
        width: '12%',
    },
    pdfSubLabelCell: {
        padding: '6px 10px',
        backgroundColor: '#f5f5f5',
        border: '1px solid #000',
        fontSize: '12px',
        fontWeight: '700',
        color: '#000',
        textAlign: 'center',
        width: '10%',
    },
    pdfValueCell: {
        padding: '8px 12px',
        border: '1px solid #000',
        fontSize: '14px',
        fontWeight: '400',
        color: '#000',
        textAlign: 'center',
        width: '28%',
    },
    pdfPatientValueCell: {
        padding: '6px 10px',
        border: '1px solid #000',
        fontSize: '14px',
        fontWeight: '400',
        color: '#000',
        textAlign: 'center',
        width: '28%',
    },
    pdfContentBox: {
        width: '100%',
        border: '1px solid #000',
        borderTop: 'none',
        minHeight: '200px',
    },
    pdfLegalSection: {
        marginTop: '24px',
        textAlign: 'center',
        paddingBottom: '16px',
    },
    pdfLegalText: {
        fontSize: '12px',
        color: '#333',
        margin: '0 0 16px',
        lineHeight: '1.6',
    },
    pdfDateText: {
        fontSize: '14px',
        color: '#000',
        fontWeight: '500',
        margin: '0 0 12px',
    },
    pdfSignature: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        fontSize: '16px',
        fontWeight: '600',
        color: '#000',
    },
    pdfStamp: {
        width: '48px',
        height: '48px',
        objectFit: 'contain',
        marginLeft: '8px',
    },
    pdfStampPlaceholder: {
        width: '48px',
        height: '48px',
        border: '2px solid #cc0000',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: '700',
        color: '#cc0000',
        marginLeft: '8px',
    },
    pdfFooterLine: {
        marginTop: '12px',
        paddingTop: '8px',
        textAlign: 'center',
        fontSize: '11px',
        color: '#666',
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
        flexWrap: 'wrap',
    },
    attachmentsTab: { 
        padding: '24px' 
    },
    emptyAttachments: { 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        padding: '60px' 
    },
    fileSection: { 
        marginBottom: '24px' 
    },
    fileSectionTitle: { 
        display: 'flex', 
        alignItems: 'center', 
        gap: '10px', 
        fontSize: '17px', 
        fontWeight: '700', 
        color: '#374151', 
        marginBottom: '12px' 
    },
    fileItem: { 
        display: 'flex', 
        alignItems: 'center', 
        gap: '14px', 
        padding: '14px 16px', 
        backgroundColor: '#f8fafc', 
        border: '1px solid #e2e8f0', 
        borderRadius: '10px', 
        marginBottom: '8px' 
    },
    fileIcon: { 
        width: '42px', 
        height: '42px', 
        borderRadius: '8px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        color: '#fff', 
        fontSize: '13px', 
        fontWeight: '700' 
    },
    fileInfo: { 
        flex: 1 
    },
    fileName: { 
        fontSize: '16px', 
        fontWeight: '600', 
        color: '#1e293b' 
    },
    fileSize: { 
        fontSize: '14px', 
        color: '#94a3b8', 
        marginTop: '2px' 
    },
    fileDownloadBtn: { 
        display: 'flex', 
        alignItems: 'center', 
        gap: '6px', 
        padding: '10px 16px', 
        backgroundColor: '#6366f1', 
        color: '#fff', 
        border: 'none', 
        borderRadius: '8px', 
        fontSize: '15px', 
        fontWeight: '600', 
        cursor: 'pointer' 
    },
    imageGridTab: { 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: '12px' 
    },
    imageItemTab: { 
        position: 'relative', 
        borderRadius: '10px', 
        overflow: 'hidden', 
        border: '1px solid #e2e8f0', 
        backgroundColor: '#f8fafc' 
    },
    imageThumbnailTab: { 
        width: '100%', 
        height: '100px', 
        objectFit: 'cover', 
        cursor: 'pointer' 
    },
    imageOverlayTab: { 
        position: 'absolute', 
        top: '6px', 
        right: '6px', 
        display: 'flex', 
        gap: '4px' 
    },
    imageBtnTab: { 
        width: '28px', 
        height: '28px', 
        backgroundColor: 'rgba(255,255,255,0.9)', 
        border: 'none', 
        borderRadius: '6px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        cursor: 'pointer', 
        color: '#374151' 
    },
    imageNameTab: { 
        padding: '8px', 
        fontSize: '13px', 
        color: '#64748b', 
        backgroundColor: '#fff', 
        whiteSpace: 'nowrap', 
        overflow: 'hidden', 
        textOverflow: 'ellipsis' 
    },
    imageModal: { 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        backgroundColor: 'rgba(0,0,0,0.85)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        zIndex: 1000, 
        padding: '20px' 
    },
    modalContent: { 
        position: 'relative', 
        maxWidth: '90vw', 
        maxHeight: '90vh', 
        backgroundColor: '#fff', 
        borderRadius: '12px', 
        overflow: 'hidden' 
    },
    modalClose: { 
        position: 'absolute', 
        top: '12px', 
        right: '12px', 
        backgroundColor: 'rgba(0,0,0,0.5)', 
        border: 'none', 
        color: '#fff', 
        borderRadius: '50%', 
        width: '36px', 
        height: '36px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        cursor: 'pointer', 
        zIndex: 10 
    },
    modalImage: { 
        maxWidth: '100%', 
        maxHeight: '70vh', 
        display: 'block' 
    },
    modalFooter: { 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: '12px 16px', 
        borderTop: '1px solid #e2e8f0' 
    },
    modalDownload: { 
        display: 'flex', 
        alignItems: 'center', 
        gap: '6px', 
        padding: '8px 16px', 
        backgroundColor: '#6366f1', 
        color: '#fff', 
        borderRadius: '6px', 
        border: 'none', 
        fontSize: '15px', 
        fontWeight: '600', 
        cursor: 'pointer' 
    },
    loading: { 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '400px', 
        color: '#64748b' 
    },
    spinner: { 
        width: '36px', 
        height: '36px', 
        border: '3px solid #e5e7eb', 
        borderTop: '3px solid #6366f1', 
        borderRadius: '50%', 
        animation: 'spin 1s linear infinite' 
    },
    error: { 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '400px', 
        color: '#ef4444' 
    },
    backButton: { 
        marginTop: '16px', 
        padding: '8px 16px', 
        backgroundColor: '#6366f1', 
        color: '#fff', 
        border: 'none', 
        borderRadius: '6px', 
        fontSize: '15px', 
        fontWeight: '600', 
        cursor: 'pointer' 
    },
};

const styleSheet = document.createElement('style');
styleSheet.textContent = '@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';
document.head.appendChild(styleSheet);

export default ViewOrder;